/**
 * Hook centralisé pour la gestion des Sessions Premium
 * =====================================================
 * 
 * RÈGLES MOA PHASE 2:
 * - Wording non anxiogène ("sessions disponibles", pas "crédits épuisés")
 * - Aucune exposition technique (Licences, coûts LLM)
 * - showQuota = false pour B2B bénéficiaires
 * - Alertes douces à 10% et 10%
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Coûts par action (pour affichage frontend uniquement)
export const SESSION_COSTS = {
  vocal_session: 3,
  feedback: 1,
  diagnostic_entry: 2,
  diagnostic_exit: 2,
  scenario: 1
};

// Wording premium non-anxiogène
export const SESSION_WORDING = {
  exhausted: {
    title: "Sessions du mois utilisées",
    subtitle: "Rechargez pour continuer votre progression",
    cta: "Recharger mes sessions"
  },
  soft_10: {
    title: "Plus que quelques sessions",
    subtitle: "Pensez à vos prochaines séances",
    cta: "Voir les options"
  },
  soft_20: {
    title: "Sessions en cours d'utilisation",
    subtitle: "Vous progressez bien !",
    cta: null
  },
  normal: {
    title: "Sessions disponibles",
    subtitle: "Continuez votre progression",
    cta: null
  }
};

/**
 * Hook principal pour les sessions
 */
export function useSessionCredits() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);  // UNIQUEMENT pour le chargement initial
  const [error, setError] = useState(null);
  
  // 🔒 P0 MOA (Jan 2026): Ref pour savoir si le chargement initial est fait
  const hasLoadedOnce = useRef(false);

  // Charger le solde (avec loading state - uniquement au montage)
  const fetchBalance = useCallback(async () => {
    try {
      // 🔒 P0 FIX: setLoading(true) UNIQUEMENT si pas encore chargé une fois
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        setBalance(null);
        setLoading(false);
        hasLoadedOnce.current = true;
        return;
      }

      const response = await axios.get(`${API}/api/sessions/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBalance(response.data);
      setError(null);
    } catch (err) {
      console.error('[useSessionCredits] Error fetching balance:', err);
      setError(err.response?.data?.detail || 'Erreur de chargement');
      // 🔒 P0 FIX: Ne pas nullifier balance sur erreur de refresh
      if (!hasLoadedOnce.current) {
        setBalance(null);
      }
    } finally {
      setLoading(false);
      hasLoadedOnce.current = true;
    }
  }, []);
  
  // 🔒 P0 MOA (Jan 2026): Refresh SILENCIEUX - ne touche PAS au loading
  // C'est cette fonction qui doit être appelée après une prise de parole d'Owen
  const refreshBalanceSilent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      console.log('[useSessionCredits] 🔄 Silent refresh (no loading state change)');
      const response = await axios.get(`${API}/api/sessions/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBalance(response.data);
      setError(null);
    } catch (err) {
      console.error('[useSessionCredits] Silent refresh error:', err);
      // Ne pas propager l'erreur - refresh silencieux
    }
  }, []);

  // Charger au montage
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Vérifier si une action est possible
  const canPerform = useCallback((actionType) => {
    if (!balance) return false;
    const cost = SESSION_COSTS[actionType] || 0;
    return balance.total_available >= cost;
  }, [balance]);

  // Obtenir le wording approprié
  const getWording = useCallback(() => {
    if (!balance) return SESSION_WORDING.normal;
    
    if (balance.alert_level === 'exhausted') return SESSION_WORDING.exhausted;
    if (balance.alert_level === 'soft_10') return SESSION_WORDING.soft_10;
    if (balance.alert_level === 'soft_20') return SESSION_WORDING.soft_20;
    return SESSION_WORDING.normal;
  }, [balance]);

  // Calculer le pourcentage restant (pour la barre de progression)
  const getPercentageRemaining = useCallback(() => {
    if (!balance || balance.monthly_quota === 0) return 0;
    return Math.round((balance.current / balance.monthly_quota) * 100);
  }, [balance]);

  // Formater la date de reset
  const getResetDateFormatted = useCallback(() => {
    if (!balance?.reset_date) return '';
    try {
      const date = new Date(balance.reset_date);
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long' 
      });
    } catch {
      return '';
    }
  }, [balance]);

  return {
    // Données brutes
    balance,
    loading,
    error,
    
    // Helpers calculés
    current: balance?.current || 0,
    monthlyQuota: balance?.monthly_quota || 0,
    bonus: balance?.bonus || 0,
    totalAvailable: balance?.total_available || 0,
    percentageRemaining: getPercentageRemaining(),
    resetDate: getResetDateFormatted(),
    
    // États
    showQuota: balance?.show_quota ?? true,
    alertLevel: balance?.alert_level || null,
    source: balance?.source || 'b2c',
    isB2B: balance?.source === 'b2b',
    
    // =========================================================================
    // PHASE 3 MOA: B2B voit son quota mais NE PEUT PAS recharger
    // =========================================================================
    canRecharge: balance?.source !== 'b2b',  // B2B = false, B2C = true
    
    // Wording
    displayText: balance?.display_text || '',
    displaySubtitle: balance?.display_subtitle || '',
    wording: getWording(),
    
    // Actions
    canPerform,
    refresh: fetchBalance,
    refreshSilent: refreshBalanceSilent,  // 🔒 P0 MOA: Refresh SANS changement de loading (pour après prise de parole Owen)
    
    // Checks rapides
    hasEnoughFor: {
      vocalSession: canPerform('vocal_session'),
      feedback: canPerform('feedback'),
      diagnostic: canPerform('diagnostic_entry')
    },
    
    // Flags d'alerte
    isExhausted: balance?.alert_level === 'exhausted',
    isLow: balance?.alert_level === 'soft_10' || balance?.alert_level === 'soft_20',
    needsRecharge: balance?.alert_level === 'exhausted' || balance?.alert_level === 'soft_10'
  };
}

export default useSessionCredits;
